/**
 * AuditDetailDrawer — single audit-row detail (STORY-008-008). BottomSheet
 * (clones SellerDrawer). Shows actor / action / entity / IP (already masked by
 * the server per ops.admin_actions.read) / user-agent / timestamp, and the
 * before→after JSON diff. READ-only: no actions.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet, GPill, Skeleton } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { useAuditRow } from "@/features/admin/audit/hooks";
import { JsonDiff } from "@/features/admin/audit/JsonDiff";

export interface AuditDetailDrawerProps {
  auditId: string | null;
  onClose: () => void;
}

function Row({ label, value }: { label: string; value: string | null | undefined }): ReactElement {
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <dt className="text-xs font-medium text-[var(--g-secondary)]">{label}</dt>
      <dd className="break-words text-sm">{value && value.length > 0 ? value : "—"}</dd>
    </div>
  );
}

export function AuditDetailDrawer({ auditId, onClose }: AuditDetailDrawerProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const { data: row, isLoading } = useAuditRow(auditId);
  const isOpen = auditId !== null;

  return (
    <BottomSheet open={isOpen} onClose={onClose} title={t("audit.detail.title")} desktop={isDesktop}>
      {isLoading || !row ? (
        <div className="flex flex-col gap-3 py-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <div data-testid="audit-detail" className="flex flex-col gap-4 overflow-y-auto">
          <div className="flex flex-wrap items-center gap-2">
            <GPill tone="primary" size="sm">{row.action}</GPill>
            <GPill tone="neutral" size="sm">{row.entity_type}</GPill>
          </div>
          <dl className="divide-y divide-[var(--g-hairline)]">
            <Row label={t("audit.detail.actor")} value={row.actor?.email ?? row.actor?.display_name ?? null} />
            <Row label={t("audit.detail.actorRole")} value={row.actor_role} />
            <Row label={t("audit.detail.action")} value={row.action} />
            <Row label={t("audit.detail.entityType")} value={row.entity_type} />
            <Row label={t("audit.detail.entityId")} value={row.entity_id} />
            <Row label={t("audit.detail.ip")} value={row.ip} />
            <Row label={t("audit.detail.userAgent")} value={row.user_agent} />
            <Row label={t("audit.detail.occurredAt")} value={new Date(row.occurred_at).toLocaleString()} />
          </dl>
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
              {t("audit.detail.changes")}
            </h3>
            <JsonDiff before={row.before} after={row.after} />
          </section>
        </div>
      )}
    </BottomSheet>
  );
}

export default AuditDetailDrawer;
